import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { EnvironmentCmsUrls } from '@core/domain/types/environment-config.types';
import { getOrientation } from '@core/infrastructure/state/core.selectors';
import { ConfigService } from '@core/services/config/config.service';
import { PlatformService } from '@core/services/platform/platform.service';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import {
    CommonStylesRenderDirective,
} from '@features/cms-render/ui/directives/common-styles-render/common-styles-render.directive';
import { AssetsSharedService } from '@features/cms-render/ui/services/assets-shared/assets-shared.service';

@Component({
    selector: 'piece-image',
    templateUrl: './piece-image.component.html',
    styleUrls: ['./piece-image.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PieceImageComponent extends CommonStylesRenderDirective implements OnInit, OnChanges {
    @Input() public element: ContentItemTree;

    @ViewChild('imgElement') public imgElement: ElementRef;

    @HostBinding('class') public aligmentClasses: string | undefined;

    @HostBinding('class.marquee')
    public isMarqueeTextAnimationEnabled: boolean;

    @HostBinding('style.--marquee-duration-desktop')
    public marqueeTextDurationDesktop: string | undefined;

    @HostBinding('style.--marquee-duration-mobile')
    public marqueeTextDurationMobile: string | undefined;

    public imageSrc$: Observable<{
        src: string;
        skeleton: string;
        alt: string;
        aspectRatio: { width: number; height: number };
    }>;

    public elementImg: HTMLImageElement = this.elRef.nativeElement as HTMLImageElement;

    constructor(
        private readonly configService: ConfigService,
        private readonly assetsSharedService: AssetsSharedService,
        private readonly elRef: ElementRef,
        private readonly renderer: Renderer2,
        private readonly store: Store,
        private readonly platformService: PlatformService,
    ) {
        super();
    }

    public ngOnInit(): void {
        super.setDefaultHostStyles();
        this.applyAlignmentClasses();
        this.setMarquee();
        this.imageSrc$ = this.store.select(getOrientation).pipe(
            map(orientation => orientation ?? 'portrait'),
            map((orientation) => {
                const assetsUrl = `${this.configService.getConfigUrl<EnvironmentCmsUrls>('cmsUrls').content}/assets/`;
                const image = orientation === 'landscape' ? this.element.settings.general.landscapeImage : this.element.settings.general.portraitImage;

                // const imageLandscape = this.element.settings.general.landscapeImage.image;
                // const imagePortrait = this.element.settings.general.portraitImage.image;

                const imageUrl = image.image;
                const imageName = imageUrl?.split('/')?.pop();
                const impolicy = this.element.settings.general.impolicy;
                const src = this.assetsSharedService.getAssetUrl(imageUrl, assetsUrl, { impolicy, enabled: true });
                const skeleton = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${image?.width} ${image?.height}"%3E%3C/svg%3E`;
                // const srcset = this.generateSrcsetService.generateSrcset(imageLandscape, imagePortrait, assetsUrl);
                // const isSsr = this.element.settings.general.preloadImage ?? false;
                // const isSsr = true;

                if (this.platformService.isBrowser()) {
                    this.elementImg.style.filter = 'initial';
                } else {
                    this.elementImg.style.filter = 'blur(5px)';
                }

                return {
                    src,
                    skeleton,
                    alt: this.element.settings.general.alt ?? imageName?.split('.')?.slice(0, -1)?.join('.'),
                    aspectRatio: { width: image.width, height: image.height },
                };
            })
        );
    }

    public ngOnChanges(): void {
        this.setObjectFit();
    }

    public setObjectFit(): void {
        const { objectFit, objectPosition } = this.element.settings.styles;

        if (!!objectFit) {
            this.renderer.setStyle(this.elRef.nativeElement, '--imageObjectFit', `${objectFit}`, 2);
        }

        if (!!objectPosition) {
            this.renderer.setStyle(this.elRef.nativeElement, '--imageObjectPosition', `${objectPosition}`, 2);
        }
    }

    // piece-image-text
    private applyAlignmentClasses(): void {
        const classes: string[] = [];
        if (this.element.settings.styles.textPositionVertical) {
            classes.push(`vertical-${this.element.settings.styles.textPositionVertical}`);
        }
        classes.push(this.element.definitionId);

        this.aligmentClasses = classes.join(' ');
    }

    // piece-image-text
    private setMarquee(): void {
        this.isMarqueeTextAnimationEnabled = !!this.element.settings?.general?.marquee?.marqueeStatus;
        if (this.isMarqueeTextAnimationEnabled) {
            const marqueeDurationDesktopValue = this.element.settings.general.marquee.marqueeDurationDesktop;
            this.marqueeTextDurationDesktop = marqueeDurationDesktopValue ? `${marqueeDurationDesktopValue}s` : '20s';
            const marqueeDurationMobileValue = this.element.settings.general.marquee.marqueeDurationMobile;
            this.marqueeTextDurationMobile = marqueeDurationMobileValue ? `${marqueeDurationMobileValue}s` : '20s';
        }
    }
}
